import { Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { CHARGE_TYPE } from '@woovi/shared';
import { convertToMoney } from '@woovi/utils';

import type { PaymentLinkActiveValue_company$key } from './__generated__/PaymentLinkActiveValue_company.graphql.ts';

type PaymentLinkActiveValueProps = {
  value?: number;
  finalValue?: number;
  valueWithDiscount?: number;
  type?: string;
  company: PaymentLinkActiveValue_company$key;
};

const PaymentLinkActiveValue = ({
  value,
  type,
  ...props
}: PaymentLinkActiveValueProps): ReactNode => {
  const finalValue = props.finalValue || value;
  const valueWithDiscount = props.valueWithDiscount || value;

  const { t } = useTranslation();

  const company = useFragment<PaymentLinkActiveValue_company$key>(
    graphql`
      fragment PaymentLinkActiveValue_company on Company {
        nameFriendly
      }
    `,
    props.company,
  );

  const initialStringContext = t('To pay');

  const endStringContext = t('to {{companyName}}', {
    companyName: company.nameFriendly,
  });

  const shouldDisplayDiscount = () => {
    const isValueWithDiscountNumber = typeof valueWithDiscount === 'number';

    if (!isValueWithDiscountNumber) {
      return false;
    }

    return value !== valueWithDiscount;
  };

  const getPaymentMethodLabel = () => {
    if (type === CHARGE_TYPE.CREDIT_CARD) {
      return t('via Credit Card');
    }

    if (type === CHARGE_TYPE.BOLETO) {
      return t('via Boleto');
    }

    return t('via Pix');
  };

  const paymentMethodLabel = getPaymentMethodLabel();

  const getMessage = () => {
    if (!finalValue) {
      return t('Define the payment value');
    }

    if (shouldDisplayDiscount()) {
      return (
        <>
          {initialStringContext}
          <Typography
            component='span'
            sx={{
              color: '#b2b2b2ff',
              textDecoration: 'line-through',
              fontSize: '24px',
              fontWeight: '800',
            }}
          >
            {convertToMoney(value)}
          </Typography>
          {convertToMoney(valueWithDiscount)} {endStringContext}{' '}
          {paymentMethodLabel}
        </>
      );
    }

    return `${initialStringContext} ${t('{{finalValue}}', {
      finalValue: convertToMoney(finalValue),
    })} ${endStringContext} ${paymentMethodLabel}`;
  };

  return (
    <Typography
      sx={{ fontSize: '24px', fontWeight: '800', textAlign: 'center' }}
    >
      {getMessage()}
    </Typography>
  );
};

export default PaymentLinkActiveValue;
